import React, { useState, useEffect } from 'react';
import { X, Smartphone, CreditCard, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { useGlobalToast } from '../../contexts/ToastContext';
import { formatCurrency } from '../../utils/currency';
import { momoService, MOMO_PROVIDERS } from '../../services/momo';
import { settingsApi } from '../../services/api';

interface MoMoPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  amount: number;
  contributionType: string;
  memberName: string;
  onPaymentComplete: (reference: string) => void;
}

export const MoMoPaymentModal: React.FC<MoMoPaymentModalProps> = ({
  isOpen,
  onClose,
  amount,
  contributionType,
  memberName,
  onPaymentComplete
}) => {
  const { error } = useGlobalToast();
  const [selectedProvider, setSelectedProvider] = useState('mtn');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStep, setPaymentStep] = useState<'select' | 'processing' | 'success' | 'failed'>('select');
  const [reference, setReference] = useState('');
  const [adminContact, setAdminContact] = useState('');

  useEffect(() => {
    let mounted = true;
    settingsApi.get().then((res: any) => {
      const contact = res?.data?.settings?.adminContact || res?.data?.adminContact;
      if (contact && mounted) setAdminContact(contact);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const providers = MOMO_PROVIDERS.map(p => ({
    id: p.code.toLowerCase(),
    name: p.name,
    code: p.code,
    ussd: p.ussdCode
  }));

  const handlePayment = async () => {
    if (!momoService.validateGhanaPhone(phoneNumber)) {
      error('Please enter a valid Ghana phone number');
      return;
    }

    setIsProcessing(true);
    setPaymentStep('processing');
    
    // Generate reference for manual transfer
    const generatedRef = `${contributionType.toUpperCase()}-${Date.now()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
    setReference(generatedRef);
    
    // Simulate processing time for better UX
    setTimeout(() => {
      setIsProcessing(false);
      setPaymentStep('success');
    }, 2000);
  };

  const handleComplete = () => {
    onPaymentComplete(reference);
    onClose();
    resetModal();
  };

  const resetModal = () => {
    setSelectedProvider('mtn');
    setPhoneNumber('');
    setIsProcessing(false);
    setPaymentStep('select');
    setReference('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Smartphone className="h-6 w-6 mr-2 text-green-600" />
            Mobile Money Payment
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Payment Details */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-blue-600">Member:</span>
                <span className="text-sm font-medium text-blue-900">{memberName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-blue-600">Type:</span>
                <span className="text-sm font-medium text-blue-900">{contributionType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-blue-600">Amount:</span>
                <span className="text-lg font-bold text-blue-900">{formatCurrency(amount)}</span>
              </div>
            </div>
          </div>

          {paymentStep === 'select' && (
            <>
              {/* Provider Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Mobile Money Provider
                </label>
                <div className="space-y-2">
                  {providers.map((provider) => (
                    <label key={provider.id} className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="provider"
                        value={provider.id}
                        checked={selectedProvider === provider.id}
                        onChange={(e) => setSelectedProvider(e.target.value)}
                        className="text-green-600 focus:ring-green-500"
                      />
                      <Smartphone className="h-5 w-5 text-green-600" />
                      <div className="flex-1">
                        <span className="font-medium">{provider.name}</span>
                        <p className="text-xs text-gray-500">USSD: {provider.ussd}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Phone Number */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Money Number
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="024 123 4567"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={handlePayment}
                disabled={!phoneNumber.trim() || isProcessing}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                {isProcessing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                ) : (
                  <CreditCard className="h-5 w-5" />
                )}
                <span>{isProcessing ? 'Processing…' : `Pay ${formatCurrency(amount)}`}</span>
              </button>
            </>
          )}

          {paymentStep === 'processing' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Payment Reference</h3>
              <p className="text-gray-600 mb-4">
                Please wait while we generate your payment reference number.
              </p>
              {reference && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                    <p className="text-sm text-blue-800">
                      Reference: <strong>{reference}</strong>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {paymentStep === 'success' && (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Reference Generated!</h3>
              <p className="text-gray-600 mb-4">
                Please use the reference below to make your {formatCurrency(amount)} payment via {providers.find(p => p.id === selectedProvider)?.name}.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800 mb-2">
                  <strong>Payment Reference:</strong>
                </p>
                <p className="text-lg font-bold text-blue-900 font-mono">{reference}</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-6">
                <div className="text-left">
                  <p className="text-sm font-medium text-orange-800 mb-2">Payment Instructions:</p>
                  <ol className="text-xs text-orange-700 space-y-1">
                    <li>1. Dial {providers.find(p => p.id === selectedProvider)?.ussd} on your phone</li>
                    <li>2. Send {formatCurrency(amount)} to the organization's MoMo number</li>
                    <li>3. Use reference: <strong>{reference}</strong></li>
                    <li>4. Admin will verify and approve your payment</li>
                  </ol>
                </div>
              </div>
              <button
                onClick={handleComplete}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors"
              >
                I Understand - Complete
              </button>
            </div>
          )}

          {paymentStep === 'failed' && (
            <div className="text-center py-8">
              <XCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Reference Generation Failed</h3>
              <p className="text-gray-600 mb-6">
                Could not generate payment reference. Please try again or contact {adminContact} for assistance.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setPaymentStep('select')}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};